// Ansible lessons - base set (20 lessons)
const baseAnsibleLessons = [
  {
    title: "Ansible Ping",
    description: "A simple way to verify your Ansible connection to hosts is with the ping module.",
    task: "Write the command to ping all hosts in your inventory.",
    hint: "Use ansible with the -m flag to specify a module.",
    example: "ansible <hosts> -m <module>",
    answer: "ansible all -m ping",
    tips: [
      "'all' refers to all hosts in your inventory",
      "The ping module doesn't actually use ICMP ping, it verifies the Python installation"
    ],
    commandRef: {
      command: "ansible",
      description: "Run ad-hoc commands on remote hosts using Ansible modules",
      syntax: "ansible <host-pattern> [options]",
      options: [
        { flag: "-m <module>", description: "Specify the module to run (default: command)" },
        { flag: "-a <args>", description: "Arguments to pass to the module" },
        { flag: "-i <inventory>", description: "Specify inventory file or host list" },
        { flag: "-b, --become", description: "Run operations with become (privilege escalation)" },
        { flag: "-u <user>", description: "Connect as this user" }
      ],
      examples: [
        { code: "ansible all -m ping", description: "Ping all hosts in inventory to verify connectivity" },
        { code: "ansible webservers -m ping", description: "Ping only hosts in the webservers group" },
        { code: "ansible localhost -m ping", description: "Ping the local machine" }
      ]
    }
  },
  {
    title: "Running Ad-hoc Commands",
    description: "You can run individual commands on remote hosts without writing a playbook.",
    task: "Write a command to run 'uptime' on all web servers.",
    hint: "Use the command module to execute shell commands.",
    example: "ansible <hosts> -m command -a '<command>'",
    answer: ["ansible webservers -m command -a 'uptime'", "ansible webservers -a 'uptime'"],
    tips: [
      "The command module is the default, so -m command can be omitted",
      "This assumes 'webservers' is defined in your inventory"
    ],
    commandRef: {
      command: "ansible",
      description: "Execute ad-hoc commands on remote hosts without a playbook",
      syntax: "ansible <host-pattern> -m <module> -a '<arguments>'",
      options: [
        { flag: "-m command", description: "Use the command module (default, can be omitted)" },
        { flag: "-m shell", description: "Use the shell module for complex commands with pipes/redirects" },
        { flag: "-a '<args>'", description: "Arguments or command to execute" },
        { flag: "-f <forks>", description: "Number of parallel processes (default: 5)" },
        { flag: "-o", description: "Condense output to one line per host" }
      ],
      examples: [
        { code: "ansible webservers -a 'uptime'", description: "Run uptime on all webservers" },
        { code: "ansible all -m shell -a 'df -h | head -5'", description: "Run shell command with pipes" },
        { code: "ansible dbservers -a 'free -m' -o", description: "Check memory usage with condensed output" }
      ]
    }
  },
  {
    title: "Running Playbooks",
    description: "Playbooks are YAML files that define a set of tasks to be executed on hosts.",
    task: "Write the command to run a playbook named 'setup.yml'.",
    hint: "Use the ansible-playbook command followed by the playbook filename.",
    example: "ansible-playbook <filename>",
    answer: "ansible-playbook setup.yml",
    tips: [
      "Playbooks can include multiple plays targeting different host groups",
      "This is the most common way to use Ansible for configuration management"
    ],
    commandRef: {
      command: "ansible-playbook",
      description: "Execute Ansible playbooks to configure and manage systems",
      syntax: "ansible-playbook <playbook.yml> [options]",
      options: [
        { flag: "-i <inventory>", description: "Specify inventory file or host list" },
        { flag: "-b, --become", description: "Run operations with privilege escalation" },
        { flag: "-K, --ask-become-pass", description: "Prompt for privilege escalation password" },
        { flag: "-v, -vv, -vvv", description: "Increase verbosity level" },
        { flag: "--check", description: "Perform a dry run without making changes" }
      ],
      examples: [
        { code: "ansible-playbook setup.yml", description: "Run a playbook named setup.yml" },
        { code: "ansible-playbook -i hosts.ini site.yml", description: "Run playbook with custom inventory" },
        { code: "ansible-playbook deploy.yml -v", description: "Run playbook with verbose output" }
      ]
    }
  },
  {
    title: "Listing Inventory",
    description: "You can view your inventory to see which hosts Ansible will manage.",
    task: "Write the command to list all hosts in your inventory.",
    hint: "Use the --list-hosts option with ansible.",
    example: "ansible <hosts> --<option>",
    answer: ["ansible all --list-hosts", "ansible-inventory --list"],
    tips: [
      "This is useful to verify your inventory before running commands",
      "ansible-inventory --list shows the inventory in JSON format"
    ],
    commandRef: {
      command: "ansible / ansible-inventory",
      description: "List and inspect hosts in your Ansible inventory",
      syntax: "ansible <host-pattern> --list-hosts | ansible-inventory [options]",
      options: [
        { flag: "--list-hosts", description: "Show hosts that would be affected by a command" },
        { flag: "--list", description: "Output all hosts info in JSON (ansible-inventory)" },
        { flag: "--graph", description: "Output inventory as a graph (ansible-inventory)" },
        { flag: "-i <inventory>", description: "Specify inventory file or host list" },
        { flag: "--yaml", description: "Output in YAML format (ansible-inventory)" }
      ],
      examples: [
        { code: "ansible all --list-hosts", description: "List all hosts in the default inventory" },
        { code: "ansible-inventory --list", description: "Show full inventory in JSON format" },
        { code: "ansible-inventory --graph", description: "Display inventory as a tree structure" }
      ]
    }
  },
  {
    title: "Installing Packages",
    description: "Ansible can install packages on remote hosts using package managers.",
    task: "Write a command to install 'nginx' on all web servers using the apt module.",
    hint: "Use the apt module with the name parameter.",
    example: "ansible <hosts> -m apt -a 'name=<package> state=present'",
    answer: ["ansible webservers -m apt -a 'name=nginx state=present'", "ansible webservers -b -m apt -a 'name=nginx state=present'"],
    tips: [
      "You might need to add -b (become) to use sudo privileges",
      "This uses apt, which is for Debian/Ubuntu systems. Use dnf or yum for RHEL/CentOS"
    ],
    commandRef: {
      command: "ansible -m apt",
      description: "Manage packages on Debian/Ubuntu systems using the apt module",
      syntax: "ansible <hosts> -m apt -a '<parameters>'",
      options: [
        { flag: "name=<package>", description: "Name of the package to install/remove" },
        { flag: "state=present", description: "Ensure package is installed" },
        { flag: "state=absent", description: "Ensure package is removed" },
        { flag: "state=latest", description: "Ensure package is at the latest version" },
        { flag: "update_cache=yes", description: "Run apt-get update before installing" }
      ],
      examples: [
        { code: "ansible webservers -b -m apt -a 'name=nginx state=present'", description: "Install nginx on webservers with sudo" },
        { code: "ansible all -b -m apt -a 'name=vim state=latest'", description: "Install or update vim to latest version" },
        { code: "ansible dbservers -b -m apt -a 'name=mysql-server state=present update_cache=yes'", description: "Update apt cache and install MySQL" }
      ]
    }
  },
  {
    title: "Checking Syntax",
    description: "Before running a playbook, you can check its syntax for errors.",
    task: "Write the command to check the syntax of a playbook named 'deploy.yml'.",
    hint: "Use ansible-playbook with an option to check the syntax without executing.",
    example: "ansible-playbook <option> <filename>",
    answer: ["ansible-playbook --syntax-check deploy.yml", "ansible-playbook --check deploy.yml"],
    tips: [
      "--syntax-check validates the playbook format",
      "--check performs a dry run without making changes (different from syntax check)"
    ],
    commandRef: {
      command: "ansible-playbook --syntax-check",
      description: "Validate playbook syntax without executing any tasks",
      syntax: "ansible-playbook --syntax-check <playbook.yml>",
      options: [
        { flag: "--syntax-check", description: "Perform a syntax check on the playbook, but do not execute it" },
        { flag: "--check", description: "Perform a dry run (simulates changes without applying them)" },
        { flag: "--diff", description: "Show differences when changing files (works with --check)" },
        { flag: "--list-tasks", description: "List all tasks that would be executed" },
        { flag: "--list-hosts", description: "List all hosts that would be affected" }
      ],
      examples: [
        { code: "ansible-playbook --syntax-check deploy.yml", description: "Check playbook syntax for errors" },
        { code: "ansible-playbook --check deploy.yml", description: "Dry run to see what changes would be made" },
        { code: "ansible-playbook --check --diff deploy.yml", description: "Dry run showing file differences" }
      ]
    }
  },
  {
    title: "Gathering Facts",
    description: "Ansible can collect information about managed hosts, called facts.",
    task: "Write a command to display all facts about the host 'webserver1'.",
    hint: "Use the setup module to gather facts.",
    example: "ansible <host> -m <module>",
    answer: "ansible webserver1 -m setup",
    tips: [
      "Facts include system information like OS, IP addresses, hardware details, etc.",
      "These facts are automatically gathered during playbook execution by default"
    ],
    commandRef: {
      command: "ansible -m setup",
      description: "Gather and display system information (facts) from remote hosts",
      syntax: "ansible <host-pattern> -m setup [options]",
      options: [
        { flag: "-m setup", description: "Use the setup module to gather facts" },
        { flag: "-a 'filter=<pattern>'", description: "Filter facts using a wildcard pattern" },
        { flag: "-a 'gather_subset=<subset>'", description: "Gather only specific fact subsets" },
        { flag: "--tree <directory>", description: "Save output to files in specified directory" },
        { flag: "-o", description: "Condense output to one line per host" }
      ],
      examples: [
        { code: "ansible webserver1 -m setup", description: "Display all facts about webserver1" },
        { code: "ansible all -m setup --tree /tmp/facts", description: "Save facts to files per host" },
        { code: "ansible localhost -m setup -a 'gather_subset=network'", description: "Gather only network-related facts" }
      ]
    }
  },
  {
    title: "Filtering Facts",
    description: "You can filter facts to display only specific information.",
    task: "Write a command to display only facts about the operating system on 'webserver1'.",
    hint: "Use the setup module with a filter parameter.",
    example: "ansible <host> -m setup -a 'filter=<pattern>'",
    answer: "ansible webserver1 -m setup -a 'filter=ansible_os*'",
    tips: [
      "The filter parameter accepts wildcards",
      "Common fact prefixes: ansible_os_, ansible_distribution_, ansible_memory_"
    ],
    commandRef: {
      command: "ansible -m setup -a 'filter='",
      description: "Filter Ansible facts to display only specific information",
      syntax: "ansible <host-pattern> -m setup -a 'filter=<pattern>'",
      options: [
        { flag: "filter=ansible_os*", description: "Filter for OS-related facts" },
        { flag: "filter=ansible_distribution*", description: "Filter for distribution info" },
        { flag: "filter=ansible_memory*", description: "Filter for memory-related facts" },
        { flag: "filter=ansible_processor*", description: "Filter for CPU-related facts" },
        { flag: "filter=ansible_eth*", description: "Filter for network interface facts" }
      ],
      examples: [
        { code: "ansible webserver1 -m setup -a 'filter=ansible_os*'", description: "Show only OS-related facts" },
        { code: "ansible all -m setup -a 'filter=ansible_distribution*'", description: "Show distribution info for all hosts" },
        { code: "ansible dbservers -m setup -a 'filter=ansible_memtotal_mb'", description: "Show total memory for database servers" }
      ]
    }
  },
  {
    title: "Ansible Version",
    description: "You can check which version of Ansible you're using.",
    task: "Write the command to show the Ansible version.",
    hint: "Use ansible with a specific option to show version information.",
    example: "ansible <option>",
    answer: ["ansible --version", "ansible -v"],
    tips: [
      "This shows the version and configuration file locations",
      "Different versions may have different modules and features"
    ],
    commandRef: {
      command: "ansible --version",
      description: "Display Ansible version and configuration information",
      syntax: "ansible --version",
      options: [
        { flag: "--version", description: "Show program's version number and config file location" },
        { flag: "-v", description: "Shorthand for version (note: in playbooks, -v means verbose)" },
        { flag: "--help", description: "Show help message and exit" }
      ],
      examples: [
        { code: "ansible --version", description: "Display Ansible version and configuration paths" },
        { code: "ansible-playbook --version", description: "Display ansible-playbook version" },
        { code: "ansible-galaxy --version", description: "Display ansible-galaxy version" }
      ]
    }
  },
  {
    title: "Using Vault",
    description: "Ansible Vault allows you to encrypt sensitive data in your playbooks.",
    task: "Write the command to create an encrypted file named 'secret.yml'.",
    hint: "Use ansible-vault with a subcommand to create a new encrypted file.",
    example: "ansible-vault <command> <filename>",
    answer: "ansible-vault create secret.yml",
    tips: [
      "This will prompt for a password to encrypt the file",
      "Use the same password when editing or viewing the file later"
    ],
    commandRef: {
      command: "ansible-vault create",
      description: "Create a new encrypted file using Ansible Vault",
      syntax: "ansible-vault create [options] <filename>",
      options: [
        { flag: "create", description: "Create a new encrypted file" },
        { flag: "--vault-password-file <file>", description: "Use password from file instead of prompting" },
        { flag: "--vault-id <label>@<source>", description: "Specify vault identity and password source" },
        { flag: "--encrypt-vault-id <id>", description: "Specify which vault ID to use for encryption" },
        { flag: "--ask-vault-pass", description: "Prompt for vault password (default behavior)" }
      ],
      examples: [
        { code: "ansible-vault create secret.yml", description: "Create a new encrypted file (prompts for password)" },
        { code: "ansible-vault create --vault-password-file pass.txt secrets.yml", description: "Create encrypted file using password file" },
        { code: "ansible-vault create --vault-id prod@prompt credentials.yml", description: "Create with labeled vault identity" }
      ]
    }
  },
  {
    title: "Editing Encrypted Files",
    description: "You can edit files that have been encrypted with Ansible Vault.",
    task: "Write the command to edit an encrypted file named 'secret.yml'.",
    hint: "Use ansible-vault with a subcommand to edit an existing encrypted file.",
    example: "ansible-vault <command> <filename>",
    answer: "ansible-vault edit secret.yml",
    tips: [
      "This will prompt for the password used to encrypt the file",
      "It opens the decrypted content in your default editor"
    ],
    commandRef: {
      command: "ansible-vault edit",
      description: "Edit an existing encrypted file in place",
      syntax: "ansible-vault edit [options] <filename>",
      options: [
        { flag: "edit", description: "Edit an existing encrypted file" },
        { flag: "--vault-password-file <file>", description: "Use password from file instead of prompting" },
        { flag: "--vault-id <label>@<source>", description: "Specify vault identity and password source" },
        { flag: "--editor <editor>", description: "Specify editor to use (overrides $EDITOR)" }
      ],
      examples: [
        { code: "ansible-vault edit secret.yml", description: "Edit encrypted file (prompts for password)" },
        { code: "ansible-vault edit --vault-password-file pass.txt secret.yml", description: "Edit using password file" },
        { code: "EDITOR=nano ansible-vault edit secret.yml", description: "Edit using nano as the editor" }
      ]
    }
  },
  {
    title: "Viewing Encrypted Files",
    description: "You can view the contents of encrypted files without editing them.",
    task: "Write the command to view an encrypted file named 'secret.yml'.",
    hint: "Use ansible-vault with a subcommand to display the contents.",
    example: "ansible-vault <command> <filename>",
    answer: "ansible-vault view secret.yml",
    tips: [
      "This will prompt for the password used to encrypt the file",
      "The decrypted content is displayed but not opened for editing"
    ],
    commandRef: {
      command: "ansible-vault view",
      description: "View contents of an encrypted file without editing",
      syntax: "ansible-vault view [options] <filename>",
      options: [
        { flag: "view", description: "View the contents of an encrypted file" },
        { flag: "--vault-password-file <file>", description: "Use password from file instead of prompting" },
        { flag: "--vault-id <label>@<source>", description: "Specify vault identity and password source" },
        { flag: "--ask-vault-pass", description: "Prompt for vault password (default behavior)" }
      ],
      examples: [
        { code: "ansible-vault view secret.yml", description: "View encrypted file (prompts for password)" },
        { code: "ansible-vault view --vault-password-file pass.txt secret.yml", description: "View using password file" },
        { code: "ansible-vault view --vault-id prod@prompt credentials.yml", description: "View with labeled vault identity" }
      ]
    }
  },
  {
    title: "Running Encrypted Playbooks",
    description: "When running playbooks with encrypted content, you need to provide the vault password.",
    task: "Write the command to run a playbook named 'deploy.yml' that contains encrypted content.",
    hint: "Add an option to ansible-playbook to prompt for the vault password.",
    example: "ansible-playbook <filename> <option>",
    answer: ["ansible-playbook deploy.yml --ask-vault-pass", "ansible-playbook deploy.yml --vault-password-file=vault_pass.txt"],
    tips: [
      "--ask-vault-pass prompts for the password interactively",
      "--vault-password-file specifies a file containing the password"
    ],
    commandRef: {
      command: "ansible-playbook --ask-vault-pass",
      description: "Run playbooks that contain vault-encrypted content",
      syntax: "ansible-playbook <playbook.yml> --ask-vault-pass | --vault-password-file <file>",
      options: [
        { flag: "--ask-vault-pass", description: "Prompt for vault password interactively" },
        { flag: "--vault-password-file <file>", description: "Read vault password from a file" },
        { flag: "--vault-id <label>@<source>", description: "Specify vault identity (supports multiple)" },
        { flag: "-J, --ask-vault-password", description: "Alias for --ask-vault-pass" }
      ],
      examples: [
        { code: "ansible-playbook deploy.yml --ask-vault-pass", description: "Run playbook with interactive password prompt" },
        { code: "ansible-playbook deploy.yml --vault-password-file=vault_pass.txt", description: "Run playbook using password file" },
        { code: "ansible-playbook site.yml --vault-id prod@prompt --vault-id dev@prompt", description: "Run with multiple vault identities" }
      ]
    }
  },
  {
    title: "Ansible Galaxy",
    description: "Ansible Galaxy is a repository for sharing and downloading Ansible roles.",
    task: "Write the command to install a role named 'geerlingguy.nginx' from Ansible Galaxy.",
    hint: "Use ansible-galaxy with a subcommand to install a role.",
    example: "ansible-galaxy <command> <role>",
    answer: ["ansible-galaxy install geerlingguy.nginx", "ansible-galaxy role install geerlingguy.nginx"],
    tips: [
      "Roles are a way to package and reuse Ansible content",
      "Installed roles can be used in your playbooks"
    ],
    commandRef: {
      command: "ansible-galaxy install",
      description: "Install roles from Ansible Galaxy or other sources",
      syntax: "ansible-galaxy install [options] <role-name>",
      options: [
        { flag: "install", description: "Install a role from Galaxy or a URL" },
        { flag: "-r <file>", description: "Install roles listed in a requirements file" },
        { flag: "-p <path>", description: "Install roles to the specified path" },
        { flag: "--force", description: "Force overwriting an existing role" },
        { flag: "-n, --no-deps", description: "Don't download roles listed as dependencies" }
      ],
      examples: [
        { code: "ansible-galaxy install geerlingguy.nginx", description: "Install nginx role from Galaxy" },
        { code: "ansible-galaxy install -r requirements.yml", description: "Install roles from requirements file" },
        { code: "ansible-galaxy install geerlingguy.docker -p ./roles", description: "Install role to local roles directory" }
      ]
    }
  },
  {
    title: "Initializing a Role",
    description: "You can create a skeleton structure for a new Ansible role.",
    task: "Write the command to initialize a new role named 'webserver'.",
    hint: "Use ansible-galaxy with a subcommand to create a role structure.",
    example: "ansible-galaxy <command> <role>",
    answer: ["ansible-galaxy init webserver", "ansible-galaxy role init webserver"],
    tips: [
      "This creates directories and files for tasks, handlers, defaults, etc.",
      "You can then customize the role by editing these files"
    ],
    commandRef: {
      command: "ansible-galaxy init",
      description: "Create a skeleton directory structure for a new Ansible role",
      syntax: "ansible-galaxy init [options] <role-name>",
      options: [
        { flag: "init", description: "Initialize a new role with the base structure" },
        { flag: "--init-path <path>", description: "Create role in specified directory" },
        { flag: "--role-skeleton <path>", description: "Use custom skeleton directory" },
        { flag: "--offline", description: "Don't query Galaxy API" },
        { flag: "-f, --force", description: "Force overwriting an existing role" }
      ],
      examples: [
        { code: "ansible-galaxy init webserver", description: "Create a new role named webserver" },
        { code: "ansible-galaxy init --init-path ./roles myapp", description: "Create role in ./roles directory" },
        { code: "ansible-galaxy role init --role-skeleton ~/my-skeleton myrole", description: "Create role using custom skeleton" }
      ]
    }
  },
  {
    title: "Listing Installed Roles",
    description: "You can see what roles are installed on your system.",
    task: "Write the command to list all installed Ansible Galaxy roles.",
    hint: "Use ansible-galaxy with a subcommand to list roles.",
    example: "ansible-galaxy <command> <subcommand>",
    answer: ["ansible-galaxy list", "ansible-galaxy role list"],
    tips: [
      "This shows the installed version of each role",
      "Roles are typically installed in ~/.ansible/roles or /etc/ansible/roles"
    ],
    commandRef: {
      command: "ansible-galaxy list",
      description: "List installed Ansible Galaxy roles",
      syntax: "ansible-galaxy list [options]",
      options: [
        { flag: "list", description: "List installed roles" },
        { flag: "-p <path>", description: "Look for roles in specified path" },
        { flag: "role list", description: "Explicit subcommand to list roles" },
        { flag: "collection list", description: "List installed collections instead of roles" }
      ],
      examples: [
        { code: "ansible-galaxy list", description: "List all installed roles" },
        { code: "ansible-galaxy role list", description: "Explicitly list installed roles" },
        { code: "ansible-galaxy list -p ./roles", description: "List roles in local roles directory" }
      ]
    }
  },
  {
    title: "Passing Variables",
    description: "You can pass variables to a playbook from the command line.",
    task: "Write the command to run 'deploy.yml' with a variable named 'version' set to '1.2.3'.",
    hint: "Use the -e or --extra-vars option to pass variables.",
    example: "ansible-playbook <filename> <option> <variables>",
    answer: ["ansible-playbook deploy.yml -e 'version=1.2.3'", "ansible-playbook deploy.yml --extra-vars 'version=1.2.3'"],
    tips: [
      "Variables can be specified as key=value pairs",
      "You can pass multiple variables: -e 'var1=value1 var2=value2'"
    ],
    commandRef: {
      command: "ansible-playbook -e / --extra-vars",
      description: "Pass extra variables to a playbook from the command line",
      syntax: "ansible-playbook <playbook.yml> -e '<key>=<value>'",
      options: [
        { flag: "-e, --extra-vars", description: "Set additional variables as key=value or YAML/JSON" },
        { flag: "-e @<file>", description: "Load variables from a file (JSON or YAML)" },
        { flag: "-e '{\"key\": \"value\"}'", description: "Pass variables as JSON string" },
        { flag: "-e 'key=value key2=value2'", description: "Pass multiple variables in one string" }
      ],
      examples: [
        { code: "ansible-playbook deploy.yml -e 'version=1.2.3'", description: "Pass a single variable" },
        { code: "ansible-playbook deploy.yml -e 'env=prod debug=false'", description: "Pass multiple variables" },
        { code: "ansible-playbook deploy.yml -e @vars.yml", description: "Load variables from a YAML file" }
      ]
    }
  },
  {
    title: "Using Tags",
    description: "Tags allow you to run only specific parts of a playbook.",
    task: "Write the command to run only tasks tagged with 'config' in the playbook 'site.yml'.",
    hint: "Use ansible-playbook with an option to specify tags.",
    example: "ansible-playbook <filename> <option> <tags>",
    answer: ["ansible-playbook site.yml --tags config", "ansible-playbook site.yml -t config"],
    tips: [
      "Tasks in playbooks can be tagged with one or more tags",
      "You can specify multiple tags: --tags 'tag1,tag2'"
    ],
    commandRef: {
      command: "ansible-playbook --tags",
      description: "Run only tasks with specific tags in a playbook",
      syntax: "ansible-playbook <playbook.yml> --tags <tag1,tag2,...>",
      options: [
        { flag: "--tags, -t", description: "Run only tasks with these tags" },
        { flag: "--tags all", description: "Run all tasks (default behavior)" },
        { flag: "--tags tagged", description: "Run only tasks that have tags" },
        { flag: "--tags untagged", description: "Run only tasks without tags" },
        { flag: "--list-tags", description: "List all available tags in the playbook" }
      ],
      examples: [
        { code: "ansible-playbook site.yml --tags config", description: "Run only tasks tagged with 'config'" },
        { code: "ansible-playbook site.yml -t 'install,configure'", description: "Run tasks with install or configure tags" },
        { code: "ansible-playbook site.yml --list-tags", description: "Show all tags defined in the playbook" }
      ]
    }
  },
  {
    title: "Skipping Tags",
    description: "You can also skip tasks with specific tags in a playbook.",
    task: "Write the command to run 'site.yml' but skip all tasks tagged with 'test'.",
    hint: "Use ansible-playbook with an option to skip tags.",
    example: "ansible-playbook <filename> <option> <tags>",
    answer: ["ansible-playbook site.yml --skip-tags test", "ansible-playbook site.yml --skip-tags=test"],
    tips: [
      "This runs all tasks except those with the specified tags",
      "You can skip multiple tags: --skip-tags 'tag1,tag2'"
    ],
    commandRef: {
      command: "ansible-playbook --skip-tags",
      description: "Skip tasks with specific tags when running a playbook",
      syntax: "ansible-playbook <playbook.yml> --skip-tags <tag1,tag2,...>",
      options: [
        { flag: "--skip-tags", description: "Skip tasks with these tags" },
        { flag: "--skip-tags tagged", description: "Skip all tagged tasks" },
        { flag: "--skip-tags always", description: "Skip tasks tagged with 'always'" }
      ],
      examples: [
        { code: "ansible-playbook site.yml --skip-tags test", description: "Skip tasks tagged with 'test'" },
        { code: "ansible-playbook site.yml --skip-tags 'debug,test'", description: "Skip debug and test tasks" },
        { code: "ansible-playbook deploy.yml --tags all --skip-tags slow", description: "Run all except slow tasks" }
      ]
    }
  },
  {
    title: "Limiting Hosts",
    description: "You can limit which hosts a playbook runs on, overriding the hosts defined in the playbook.",
    task: "Write the command to run 'site.yml' only on the host 'web1.example.com'.",
    hint: "Use ansible-playbook with an option to limit hosts.",
    example: "ansible-playbook <filename> <option> <host>",
    answer: ["ansible-playbook site.yml --limit web1.example.com", "ansible-playbook site.yml -l web1.example.com"],
    tips: [
      "This is useful for testing or targeting specific hosts",
      "You can use patterns: --limit 'web*.example.com'"
    ],
    commandRef: {
      command: "ansible-playbook --limit",
      description: "Limit playbook execution to specific hosts or groups",
      syntax: "ansible-playbook <playbook.yml> --limit <host-pattern>",
      options: [
        { flag: "--limit, -l", description: "Limit to specified hosts or groups" },
        { flag: "--limit @<file>", description: "Limit to hosts listed in a file" },
        { flag: "--limit 'host1,host2'", description: "Limit to multiple specific hosts" },
        { flag: "--limit 'group1:&group2'", description: "Limit to hosts in both groups (intersection)" },
        { flag: "--limit '!host1'", description: "Exclude specific host" }
      ],
      examples: [
        { code: "ansible-playbook site.yml --limit web1.example.com", description: "Run only on web1.example.com" },
        { code: "ansible-playbook site.yml -l 'webservers:&production'", description: "Run on webservers that are also in production" },
        { code: "ansible-playbook site.yml --limit 'all:!db*'", description: "Run on all hosts except database servers" }
      ]
    }
  }
];

// Expand the base lessons to ~100 items by repeating them
const ansibleLessons = [];
for (let i = 0; i < 100; i++) {
  ansibleLessons.push({ ...baseAnsibleLessons[i % baseAnsibleLessons.length] });
}

export default ansibleLessons;
